import { z } from 'zod'
import { giftCardCartDetailsSchema } from './gift-card'
import { iso8601DateSchema, stringSchema } from './common'
import { performanceSchema, ticketCategorySchema } from './booking'

const venueSchema = z.object({
  name: stringSchema.nullish(),
  address: stringSchema.nullish(),
  latitude: z.number().nullish(),
  longitude: z.number().nullish(),
})

const cartItemSchema = z.object({
  id: stringSchema.min(1),
  type: stringSchema.min(1),
  createdAt: z.string().nullish(),
  expiresAt: z.number().nullish(),
  paymentExpiresAt: z.number().nullish(),
  dateTime: stringSchema.nullish(),
  image: stringSchema.nullish(),
  title: stringSchema.min(1),
  startDate: iso8601DateSchema.nullish(),
  startTime: stringSchema.nullish(),
  productId: stringSchema.nullish(),
  productTitle: stringSchema.nullish(),
  mandatoryHotel: z.boolean().default(false),
  packageId: stringSchema.nullish(),
  url: stringSchema.nullish(),
  venue: venueSchema,
  ticketCategory: ticketCategorySchema.nullish(),
  performance: performanceSchema.nullish(),
  giftCard: giftCardCartDetailsSchema.nullish(),
  variations: z.array(
    z.object({
      id: stringSchema.nullish(),
      price: z.number(),
      // `discount` is coupon/gift-card money-off — DO NOT conflate with the
      // price-drop fields below (BOAT-1739), which are the per-unit "was" price
      // and drop % snapshotted at add-to-cart. Both null unless a genuine drop.
      discount: z.number().nullish(),
      wasPrice: z.number().nullish(),
      discountPercent: z.number().nullish(),
      quantity: z.number(),
      name: stringSchema.nullish(),
      description: stringSchema.nullish(),
    }),
  ),
})

const hotelGuestRoomSchema = z.object({
  roomTypeId: stringSchema.nullish(),
  adults: z.coerce.number().default(1),
  children: z.coerce.number().default(0),
  childrenAges: z.array(z.coerce.number()).default([]),
})

const hotelPricingSchema = z.object({
  net: z.coerce.number().nullish(),
  currency: stringSchema.nullish(),
  displayNet: z.coerce.number().default(0),
  displayTotal: z.coerce.number().default(0),
  displayCurrency: stringSchema.nullish(),
  sourceNet: z.coerce.number().nullish(),
  sourceCurrency: stringSchema.nullish(),
  margin: z.coerce.number().nullish(),
})

/**
 * Ticket plus this hotel, quoted as one trip. Only the package endpoints fill it
 * in — the plain cart hotel search leaves it undefined.
 */
const packagePricingSchema = z.object({
  ticketTotal: z.coerce.number().default(0),
  hotelTotal: z.coerce.number().default(0),
  total: z.coerce.number().default(0),
  perPerson: z.coerce.number().default(0),
  travellers: z.coerce.number().default(1),
  nights: z.coerce.number().default(0),
  currency: stringSchema.nullish(),
})

const hotelOfferSchema = z
  .object({
    package: packagePricingSchema.nullish(),
    provider: stringSchema.nullish(),
    hotelId: stringSchema.min(1),
    name: stringSchema.min(1),
    address: stringSchema.nullish(),
    city: stringSchema.nullish(),
    latitude: z.coerce.number().nullish(),
    longitude: z.coerce.number().nullish(),
    description: stringSchema.nullish(),
    roomInfo: stringSchema.nullish(),
    locationInfo: stringSchema.nullish(),
    images: z.array(stringSchema.min(1)).default([]),
    rooms: z.array(z.any()).default([]),
    stars: z.coerce.number().nullish(),
    breakfast: z.boolean().default(false),
    wifi: z.boolean().default(false),
    checkIn: stringSchema.nullish(),
    checkOut: stringSchema.nullish(),
    supplierCancellationPolicy: z.any().nullish(),
    pricing: hotelPricingSchema,
    raw: z.any().nullish(),
  })
  .passthrough()

const hotelSearchCriteriaSchema = z
  .object({
    provider: stringSchema.nullish(),
    date: stringSchema.min(1),
    nights: z.coerce.number().min(1),
    rooms: z.record(z.string(), z.coerce.number()).default({}),
    guestRooms: z.array(hotelGuestRoomSchema).default([]),
    occupancy: z.array(z.any()).default([]),
    city: stringSchema.nullish(),
    cityId: z.union([stringSchema, z.number()]).nullish(),
    currency: stringSchema.nullish(),
    language: stringSchema.nullish(),
  })
  .passthrough()

const hotelCitySchema = z.object({
  id: stringSchema.min(1),
  name: stringSchema.min(1),
  countryCode: stringSchema.nullish(),
  latitude: z.coerce.number().nullish(),
  longitude: z.coerce.number().nullish(),
})

const hotelCityListSchema = z.object({
  provider: stringSchema.nullish(),
  items: z.array(hotelCitySchema).default([]),
})

const hotelSearchResultSchema = z.object({
  provider: stringSchema.nullish(),
  criteria: hotelSearchCriteriaSchema,
  items: z.array(hotelOfferSchema),
  raw: z.any().nullish(),
})

const cartAddOnSchema = z
  .object({
    id: stringSchema.min(1),
    type: z.literal('hotel'),
    packageId: stringSchema.nullish(),
    provider: stringSchema.nullish(),
    search: hotelSearchCriteriaSchema,
    offer: hotelOfferSchema,
    rooms: z.record(z.string(), z.coerce.number()).default({}),
    guestRooms: z.array(hotelGuestRoomSchema).default([]),
    pricing: hotelPricingSchema,
    cancellationPolicy: z.any().nullish(),
    selectedAt: stringSchema.nullish(),
  })
  .passthrough()

const appliedGiftCardSchema = z.object({
  code: stringSchema.min(1),
  status: stringSchema.min(1),
  appliedAmount: z.coerce.number(),
  remainingBalance: z.coerce.number().nullish(),
})

const invalidGiftCardSchema = z.object({
  code: stringSchema.min(1),
  status: stringSchema.min(1),
  errors: z.array(stringSchema.min(1)).default([]),
})

/** The "your package" panel: what the cart holds, priced as a whole. */
const packageSummarySchema = z.object({
  complete: z.boolean().default(false),
  pricing: packagePricingSchema,
  /** Search defaults for the package's own fixture, not the whole basket. */
  defaults: hotelSearchCriteriaSchema.nullish(),
  tickets: z.object({
    total: z.coerce.number().default(0),
    travellers: z.coerce.number().default(1),
    currency: stringSchema.nullish(),
    items: z
      .array(
        z.object({
          productName: stringSchema.nullish(),
          ticketCategory: stringSchema.nullish(),
          quantity: z.coerce.number().default(0),
          dateTime: stringSchema.nullish(),
          startDate: stringSchema.nullish(),
          startTime: stringSchema.nullish(),
          venueName: stringSchema.nullish(),
          venueCity: stringSchema.nullish(),
        }),
      )
      .default([]),
  }),
  hotel: z
    .object({
      id: stringSchema.nullish(),
      name: stringSchema.nullish(),
      address: stringSchema.nullish(),
      city: stringSchema.nullish(),
      image: stringSchema.nullish(),
      nights: z.coerce.number().default(0),
      guestRooms: z.array(hotelGuestRoomSchema).default([]),
      total: z.coerce.number().default(0),
    })
    .nullish(),
})

// Ticket Protection on the cart (BOAT-1975): offered when the site sells it
// and the cart has a covered line; `total` already includes it when selected.
const cartTicketProtectionSchema = z.object({
  available: z.boolean().default(false),
  selected: z.boolean().default(false),
  rate: z.coerce.number().nullish(),
  eligibleSubtotal: z.coerce.number().default(0),
  amount: z.coerce.number().default(0),
  eligibleItemIds: z.array(z.string()).default([]),
})

const cartSchema = z.object({
  currency: stringSchema.nullish(),
  id: stringSchema.min(1),
  discount: z.number(),
  subtotal: z.number(),
  total: z.number(),
  giftCardTotal: z.coerce.number().default(0),
  totalPayable: z.coerce.number().default(0),
  appliedGiftCards: z.array(appliedGiftCardSchema).default([]),
  invalidGiftCards: z.array(invalidGiftCardSchema).default([]),
  items: z.array(cartItemSchema),
  addOns: z.array(cartAddOnSchema).default([]),
  ticketProtection: cartTicketProtectionSchema.default({
    available: false,
    selected: false,
    rate: null,
    eligibleSubtotal: 0,
    amount: 0,
    eligibleItemIds: [],
  }),
})

export {
  cartAddOnSchema,
  cartItemSchema,
  cartSchema,
  cartTicketProtectionSchema,
  hotelCityListSchema,
  hotelCitySchema,
  hotelGuestRoomSchema,
  hotelOfferSchema,
  hotelPricingSchema,
  hotelSearchCriteriaSchema,
  hotelSearchResultSchema,
  packagePricingSchema,
  packageSummarySchema,
}
